import React from 'react';
import _compact from 'lodash/compact';
import _map from 'lodash/map';
import { stringify } from 'query-string';

import { jsonParse, jsonStringify } from '@lyearn/core/utils/json';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useLinking from '@/hooks/useLinking';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import { IntegrationScopeEnum } from '@/types/schema';

import { useCreateOutlookCalendarEventFromMeetingMutation } from '../graphql/mutations/generated/createOutlookCalendarEventFromMeeting';
import { CalendarEventProps } from './types';

export function getOutlookCalendarLink(args: CalendarEventProps) {
  const { title, start, organiser, end, description, guests } = args;

  const details: { [key: string]: string } = {
    path: '/calendar/action/compose',
    rru: 'addevent',
    startdt: new Date(start).toISOString(),
    subject: title,
    body: description ?? '',
  };

  if (end) {
    details.enddt = new Date(end).toISOString();
  }

  if (guests?.length) {
    const guestIds: string[] = [organiser.emailId, ..._compact(_map(guests, (it) => it.emailId))];
    details.add = guestIds.join();
  }

  return `https://outlook.live.com/calendar/action/compose?${stringify(details)}`;
}

// deprecated from be
export function useOutlookCalendarMeetingEvent() {
  const [, executeCreateEvent] = useCreateOutlookCalendarEventFromMeetingMutation();
  const user = useUserContext();
  const { openLink } = useLinking();
  const { t } = useTranslation(['Meeting']);

  const { showSnackbar, showErrorSnackbar } = useSnackbar();

  const createOutlookMeetingEvent = React.useCallback(
    async (meetingId: string) => {
      const response = await executeCreateEvent({
        data: {
          meetingId,
          userId: user.id,
          relayState: jsonStringify({
            redirectUrl: `${window.location.protocol}//${window.location.host}/integration-status/index.html?success=true`,
            failureUrl: `${window.location.protocol}//${window.location.host}/integration-status/index.html?success=false`,
            scope: IntegrationScopeEnum.User.toLowerCase(),
          }).data,
        },
      });

      if (response.error || !response.data) {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
        return;
      }

      if (response.data.createOutlookCalendarEventFromMeeting.success) {
        showSnackbar({ message: t('Common:CreatingOutlookEventSuccess') });
        return;
      }

      const eventListener = (event: MessageEvent<string>) => {
        const data = jsonParse(event.data).data as
          | {
              type: string;
              success: boolean;
              errorCode?: string;
            }
          | undefined;

        if (!data || data.type !== 'integration-status') {
          return;
        }

        if (data.success) {
          showSnackbar({ message: t('Common:CreatingOutlookEventSuccess') });
        } else {
          showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
        }
      };

      window.addEventListener('message', eventListener, false);
      openLink(response.data.createOutlookCalendarEventFromMeeting.redirectUrl!);

      return () => {
        window.removeEventListener('message', eventListener, false);
      };
    },
    [executeCreateEvent, openLink, showErrorSnackbar, showSnackbar, t, user.id],
  );

  return { createOutlookMeetingEvent };
}
